import { useState, useRef, useEffect, useCallback } from 'react';
import { isEmpty } from 'lodash';
import { MAX_CLOSED_MESSAGES } from '../../constants';
import { isMessageExpired } from '../../utils';
import { readState, persistState } from './utils/localStorage';
import { shouldShow, isInboxRealtimeMessage } from './utils/validations';
import { parseNotification } from './utils/notificationParser';

const defaultInitialState = {
    message: {},
    isOpen: false,
    isMinimized: false,
    closedCount: 0,
};

/**
 * Returns the hook's initial state from local storage.
 * if local storage is empty or the stored message expired - returns the default state.
 * @returns {{
 *  isOpen: boolean,
 *  isMinimized: boolean,
 *  closedCount: number,
 *  message: {
 *      sender: {
 *          username: string,
 *          profilePictureUrl: string?
 *      },
 *      sentAt: number,
 *      conversationUrl: string?
 *  } | {}
 * }} initialState
 */
const readInitialState = () => {
    const state = readState();

    if (isEmpty(state)) {
        return defaultInitialState;
    }

    if (isMessageExpired(state.message)) {
        return {
            ...defaultInitialState,
            closedCount: state.closedCount,
        };
    }

    return state;
};

/**
 * Returns state and actions of `EncourageSellerReply` component.
 * Returned object structure:
 * ```
 * {
 *  close               // transition the component to closed state
 *  expand              // transition the component to expanded state
 *  minimize            // transition the component to minmized state
 *  clear               // function to clear the current state
 *  onNewNotification   // function to update the compoennt state when a message notification is received
 *  message             // the current displayed message details
 *  isOpen              // is the component visible
 *  isMinimized         // is the component minimzed
 * }
 * ```
 * @returns {{
 *  close: () => void,
 *  expand: () => void,
 *  minimize: () => void,
 *  clear: () => void,
 *  onNewNotification: (notification: Object) => void
 *  message: {
 *      sender: {
 *          username: string,
 *          profilePictureUrl: string?
 *      },
 *      sentAt: number,
 *      conversationUrl: string?
 *  } | {},
 *  isOpen: boolean,
 *  isMinimized: boolean
 * }}
 */
const useEncourageSellerReplyController = () => {
    const [initialState] = useState(() => readInitialState());

    const [message, setMessage] = useState(initialState.message);

    const [isOpen, setIsOpen] = useState(initialState.isOpen);

    const [isMinimized, setIsMinimized] = useState(initialState.isMinimized);

    const [closedCount, setClosedCount] = useState(initialState.closedCount);

    const firstRender = useRef(true);
    useEffect(() => {
        if (!firstRender.current) {
            persistState({ message, isOpen, isMinimized, closedCount });
        }

        firstRender.current = false;
    }, [message, isOpen, isMinimized, closedCount]);

    const close = useCallback(() => {
        incrementClosedCounter();
        setIsOpen(false);
        setIsMinimized(false);
    }, []);

    const expand = useCallback(() => {
        setIsOpen(true);
        setIsMinimized(false);
    }, []);

    const minimize = useCallback(() => {
        setIsOpen(true);
        setIsMinimized(true);
    }, []);

    const clear = useCallback(() => {
        setIsOpen(false);
    }, []);

    const onNewNotification = (notification) => {
        const parsedNotification = parseNotification(notification);

        if (!shouldHandleNotification(parsedNotification)) {
            return;
        }

        if (!isOpen) {
            return updateNewMessage(parsedNotification);
        }

        // check for updates in local storage
        const { isOpen: storageIsOpen } = readState();
        if (!storageIsOpen) {
            return updateNewMessage(parsedNotification);
        }
    };

    const updateNewMessage = (msg) => {
        setMessage({
            sender: msg.sender,
            sentAt: msg.sentAt,
            conversationUrl: msg.conversationUrl,
        });
        expand();
    };

    const shouldHandleNotification = (notification) =>
        isInboxRealtimeMessage(notification) &&
        shouldShow(notification) &&
        !isMessageExpired(notification) &&
        closedCount < MAX_CLOSED_MESSAGES;

    const incrementClosedCounter = () => setClosedCount((count) => count + 1);

    return {
        close,
        expand,
        minimize,
        clear,
        onNewNotification,
        message,
        isOpen,
        isMinimized,
    };
};

export default useEncourageSellerReplyController;
